import { Button } from "@/components/ui/button";
import { AvatarStudio, randomAvatarColors } from "./AvatarStudio";
import { SPATIAL_AV_TIP } from "./constants";
import { InWorldSheet } from "./InWorldSheet";
import type { AvatarColors } from "./world-engine";

type OnboardingModalProps = {
  name: string;
  onNameChange: (name: string) => void;
  colors: AvatarColors;
  onColorChange: (key: keyof AvatarColors, value: string) => void;
  onColorsReplace?: (colors: AvatarColors) => void;
  entered: boolean;
  onEnter: () => void;
  /** Close sheet when editing look after enter (X / backdrop). */
  onClose?: () => void;
};

/**
 * First-run welcome stays a centered modal.
 * In-world “edit look” uses a side sheet so HQ stays visible.
 */
export function OnboardingModal({
  name,
  onNameChange,
  colors,
  onColorChange,
  onColorsReplace,
  entered,
  onEnter,
  onClose,
}: OnboardingModalProps) {
  const studio = (
    <AvatarStudio
      layout={entered ? "tray" : "modal"}
      name={name}
      onNameChange={onNameChange}
      colors={colors}
      onColorChange={onColorChange}
      onColorsReplace={onColorsReplace}
      onRandomize={onColorsReplace ? () => onColorsReplace(randomAvatarColors()) : undefined}
      showPreview={!entered}
    />
  );

  if (entered) {
    return (
      <InWorldSheet
        open
        title="Edit your look"
        subtitle="Tweaks apply for everyone in HQ."
        onClose={onClose ?? onEnter}
        footer={
          <Button type="button" onClick={onEnter} className="w-full sm:w-auto">
            Done
          </Button>
        }
      >
        {studio}
        <p className="mt-3 text-[11px] text-muted-foreground">
          Arrow keys or click · E wave · 1–6 actions · {SPATIAL_AV_TIP}
        </p>
      </InWorldSheet>
    );
  }

  return (
    <div className="absolute inset-0 z-[80] flex items-center justify-center bg-[rgba(0,24,71,0.7)] backdrop-blur-[7px]">
      <div className="flex w-[min(432px,94vw)] flex-col items-center gap-[13px] rounded-2xl bg-white px-[30px] py-[26px] text-center shadow-[var(--shadow-lg)]">
        <img src="/assets/nymbl-fox-blue.png" alt="Atlas the fox" className="h-11" />
        <div>
          <div
            className="text-[21px] font-bold text-[var(--fg-1)]"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Welcome to Nymbl Vyllage
          </div>
          <div className="mt-1.5 text-[12.5px] leading-relaxed text-[var(--fg-2)]">
            Real multiplayer HQ. {SPATIAL_AV_TIP}.
          </div>
        </div>

        {studio}

        <button
          type="button"
          onClick={onEnter}
          className="w-full cursor-pointer rounded-full bg-[#001847] py-3 text-sm font-bold tracking-wide text-white hover:bg-[#000F2E]"
          style={{ fontFamily: "var(--font-body)" }}
        >
          Enter The Office
        </button>
        <div className="text-[11px] text-[var(--fg-3)]">
          Arrow keys or click · E wave · 1–6 actions · {SPATIAL_AV_TIP}
        </div>
      </div>
    </div>
  );
}
